import React from "react";
import { TextField, EyeOnIcon, EyeOffIcon } from "@fmc-digital/ui";

export interface LoginFieldsProps {
  /**
   * Email/username value
   */
  username: string;
  /**
   * Password value
   */
  password: string;
  /**
   * Whether password is visible
   */
  showPassword: boolean;
  /**
   * Username change handler
   */
  onUsernameChange: (value: string) => void;
  /**
   * Password change handler
   */
  onPasswordChange: (value: string) => void;
  /**
   * Toggle password visibility handler
   */
  onTogglePasswordVisibility: () => void;
  /**
   * Form submission handler
   */
  onSubmit: (e: React.SubmitEvent<HTMLFormElement>) => void;
  /**
   * Whether fields are disabled
   */
  disabled?: boolean;
  /**
   * Optional children to render inside the form (e.g., button)
   */
  children?: React.ReactNode;
  emailError?: string | null;
  passwordError?: string | null;
}

/**
 * LoginFields molecule component
 * Contains the email and password input fields for login
 * Uses CSS-based responsive styling instead of JavaScript conditionals
 * Matches Figma design specifications
 */
export const LoginFields: React.FunctionComponent<LoginFieldsProps> = ({
  username,
  password,
  showPassword,
  onUsernameChange,
  onPasswordChange,
  onTogglePasswordVisibility,
  onSubmit,
  disabled = false,
  children,
  emailError,
  passwordError,
}) => {
  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 md:space-y-6"
      data-testid="login-fields"
    >

      <div className="form-group">
        <TextField
          id="login-email"
          type="text"
          value={username}
          onChange={(e) => onUsernameChange(e.target.value)}
          label="Email"
          placeholder="Email"
          variant="outlined"
          fullWidth
          autoComplete="username"
          testID="email-input"
          disabled={disabled}
          error={emailError ?? undefined}
        />
      </div>

      <div className="form-group">
        <TextField
          id="login-password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          label="Password"
          placeholder="Password"
          variant="outlined"
          fullWidth
          autoComplete="current-password"
          testID="password-input"
          disabled={disabled}
          error={passwordError ?? undefined}
          endIcon={
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTogglePasswordVisibility();
              }}
              className="p-0 m-0 border-0 bg-transparent cursor-pointer hover:opacity-70 focus:outline-none inline-flex items-center justify-center"
              data-testid={showPassword ? "passwordEye" : "passwordEyeSlash"}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOnIcon className="w-6 h-6" />
              ) : (
                <EyeOffIcon className="w-6 h-6" />
              )}
            </button>
          }
        />
      </div>

      {children}
    </form>
  );
};
